// src/services/leaveService.js
import { api } from './api';

// ============ EMPLOYEE APIs ============

/**
 * Get my leave balance
 * @param {number} year - Optional year filter
 */
export const getMyBalance = async (year) => {
  try {
    const url = year ? `/api/leave/my-balance/?year=${year}` : '/api/leave/my-balance/';
    const response = await api.get(url);
    return response;
  } catch (error) {
    ('Get balance error:', error);
    throw error;
  }
};

/**
 * Get my leave summary (dashboard)
 */
export const getMySummary = async () => {
  try {
    const response = await api.get('/api/leave/my-summary/');
    return response;
  } catch (error) {
    ('Get summary error:', error);
    throw error;
  }
};

/**
 * Get my leave requests with filters
 * @param {Object} params - Filter parameters
 */
export const getMyRequests = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/api/leave/my-requests/${queryParams ? `?${queryParams}` : ''}`;
    const response = await api.get(url);
    return response;
  } catch (error) {
    ('Get requests error:', error);
    throw error;
  }
};

/**
 * Get single leave request
 * @param {number} id - Leave request ID
 */
export const getLeaveRequest = async (id) => {
  try {
    const response = await api.get(`/api/leave/my-requests/${id}/`);
    return response;
  } catch (error) {
    ('Get leave request error:', error);
    throw error;
  }
};

/**
 * Apply for leave
 * @param {FormData|Object} data - Leave request data
 */
export const applyLeave = async (data) => {
  try {
    const response = await api.post('/api/leave/my-requests/', data);
    return response;
  } catch (error) {
    ('Apply leave error:', error);
    throw error;
  }
};

/**
 * Update leave request (draft/pending)
 * @param {number} id - Leave request ID
 * @param {FormData|Object} data - Updated data
 */
export const updateLeaveRequest = async (id, data) => {
  try {
    const response = await api.patch(`/api/leave/my-requests/${id}/`, data);
    return response;
  } catch (error) {
    ('Update leave error:', error);
    throw error;
  }
};

/**
 * Delete leave request (draft only)
 * @param {number} id - Leave request ID
 */
export const deleteLeaveRequest = async (id) => {
  try {
    const response = await api.delete(`/api/leave/my-requests/${id}/`);
    return response;
  } catch (error) {
    ('Delete leave error:', error);
    throw error;
  }
};

/**
 * Submit draft for approval
 * @param {number} id - Leave request ID
 */
export const submitLeaveRequest = async (id) => {
  try {
    const response = await api.post(`/api/leave/my-requests/${id}/submit/`);
    return response;
  } catch (error) {
    ('Submit leave error:', error);
    throw error;
  }
};

/**
 * Cancel leave request
 * @param {number} id - Leave request ID
 */
export const cancelLeaveRequest = async (id) => {
  try {
    const response = await api.post(`/api/leave/my-requests/${id}/cancel/`);
    return response;
  } catch (error) {
    ('Cancel leave error:', error);
    throw error;
  }
};

/**
 * Withdraw leave request
 * @param {number} id - Leave request ID
 */
export const withdrawLeaveRequest = async (id) => {
  try {
    const response = await api.post(`/api/leave/my-requests/${id}/withdraw/`);
    return response;
  } catch (error) {
    ('Withdraw leave error:', error);
    throw error;
  }
};

// ============ HR/ADMIN APIs ============

/**
 * Get all pending requests (HR only)
 * @param {Object} params - Filter parameters
 */
export const getPendingRequests = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/api/leave/admin/pending/${queryParams ? `?${queryParams}` : ''}`;
    const response = await api.get(url);
    return response;
  } catch (error) {
    ('Get pending requests error:', error);
    throw error;
  }
};
export const getApprovedRequests = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/api/leave/admin/approved/${queryParams ? `?${queryParams}` : ''}`;
    const response = await api.get(url);
    return response;
  } catch (error) {
    ('Get approved requests error:', error);
    throw error;
  }
};
export const getRejectedRequests = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/api/leave/admin/rejected/${queryParams ? `?${queryParams}` : ''}`;
    const response = await api.get(url);
    return response;
  } catch (error) {
    ('Get rejected requests error:', error);
    throw error;
  }
};
export const getHrDashboard = async () => {
  try {
    const response = await api.get('/api/leave/admin/dashboard/');
    return response;
  } catch (error) {
    ('Get dashboard error:', error);
    throw error;
  }
};
export const approveLeave = async (id, data) => {
  try {
    const response = await api.post(`/api/leave/admin/${id}/approve/`, data);
    return response;
  } catch (error) {
    ('Approve leave error:', error);
    throw error;
  }
};

export const rejectLeave = async (id, data) => {
  try {
    const response = await api.post(`/api/leave/admin/${id}/reject/`, data);
    return response;
  } catch (error) {
    ('Reject leave error:', error);
    throw error;
  }
};
export const reopenLeave = async (id) => {
  try {
    const response = await api.post(`/api/leave/admin/${id}/reopen/`);
    return response;
  } catch (error) {
    ('Reopen leave error:', error);
    throw error;
  }
};

export const getEmployeeLeaves = async (employeeId, params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/api/leave/admin/employee/${employeeId}/leaves/${queryParams ? `?${queryParams}` : ''}`;
    const response = await api.get(url);
    return response;
  } catch (error) {
    ('Get employee leaves error:', error);
    throw error;
  }
};

export const adjustBalance = async (employeeId, data) => {
  try {
    const response = await api.patch(`/api/leave/admin/employee/${employeeId}/balance/adjust/`, data);
    return response;
  } catch (error) {
    ('Adjust balance error:', error);
    throw error;
  }
};

export const bulkApprove = async (data) => {
  try {
    const response = await api.post('/api/leave/admin/bulk/approve/', data);
    return response;
  } catch (error) {
    ('Bulk approve error:', error);
    throw error;
  }
};

export const bulkReject = async (data) => {
  try {
    const response = await api.post('/api/leave/admin/bulk/reject/', data);
    return response;
  } catch (error) {
    ('Bulk reject error:', error);
    throw error;
  }
};

export const getBalanceHistory = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/api/leave/admin/history/${queryParams ? `?${queryParams}` : ''}`;
    const response = await api.get(url);
    return response;
  } catch (error) {
    ('Get balance history error:', error);
    throw error;
  }
};

// ============ PUBLIC APIs (Email Links) ============


export const approveViaEmail = async (token) => {
  try {
    const response = await api.post(`/api/leave/email/approve/${token}/`);
    return response;
  } catch (error) {
    ('Approve via email error:', error);
    throw error;
  }
};


export const rejectViaEmail = async (token, data) => {
  try {
    const response = await api.post(`/api/leave/email/reject/${token}/`, data);
    return response;
  } catch (error) {
    ('Reject via email error:', error);
    throw error;
  }
};

/**
 * View via email link (public)
 * @param {string} token - Approval token
 */
export const viewViaEmail = async (token) => {
  try {
    const response = await api.get(`/api/leave/email/view/${token}/`);
    return response;
  } catch (error) {
    ('View via email error:', error);
    throw error;
  }
};